/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import a11yKeyPress from 'js/lib/a11yKeyPress';

import { Button, Grid, Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { PlayIcon } from '@coursera/cds-icons';

import Markdown from 'bundles/cms/components/Markdown';
import type { ProgramTestimonialFragment } from 'bundles/xddp/__generated__/contentfulTypes';
import Overlay from 'bundles/xddp/components/shared/Overlay';

import _t from 'i18n!nls/xddp';

import 'css!bundles/xddp/components/__styles__/shared/VideoStory';

type State = {
  shouldPlayBackVideo: boolean;
};

const styles: Record<string, Interpolation<Theme>> = {
  videoPlayerContainer: css`
    @media (min-width: 288px) {
      height: 128px;
    }

    @media (min-width: 576px) {
      height: 324;
    }

    @media (min-width: 792px) {
      height: 445px;
    }

    @media (min-width: 1008px) {
      height: 567px;
    }

    @media (min-width: 1152px) {
      height: 648px;
    }
  `,
  videoPlayerIcon: css`
    && {
      height: 40px;
      width: 40px;
    }
  `,
};

class VideoStory extends React.Component<ProgramTestimonialFragment, State> {
  state = {
    shouldPlayBackVideo: false,
  };

  enableVideoPlayback = () => {
    const { shouldPlayBackVideo } = this.state;

    if (!shouldPlayBackVideo) {
      this.setState(() => ({
        shouldPlayBackVideo: true,
      }));
    }
  };

  disableVideoPlayback = () => {
    const { shouldPlayBackVideo } = this.state;

    if (shouldPlayBackVideo) {
      this.setState(() => ({
        shouldPlayBackVideo: false,
      }));
    }
  };

  renderVideoScreen = () => {
    const { image, videoEmbedUrl, entryTitle } = this.props;
    const { shouldPlayBackVideo } = this.state;

    return (
      <div
        className="rc-VideoStory"
        onClick={this.enableVideoPlayback}
        onKeyPress={(event) => a11yKeyPress(event, this.enableVideoPlayback)}
        role="button"
        tabIndex={-1}
      >
        <div className="video-placeholder-container">
          <div
            className="play-icon"
            role="button"
            aria-label={_t('Video presentation, #{entryTitle}', { entryTitle })}
            tabIndex={0}
          >
            <PlayIcon color="invert" css={styles.videoPlayerIcon} />
          </div>
          <div className="video-overlay" />
          <Imgix className="video-placeholder" src={image?.url || ''} alt={entryTitle || ''} />
        </div>
        {shouldPlayBackVideo && (
          <Overlay handleClose={this.disableVideoPlayback} isOpen={shouldPlayBackVideo}>
            <div css={styles.videoPlayerContainer}>
              <iframe
                title="Youtube Embedded Video"
                width="100%"
                height="100%"
                src={videoEmbedUrl || ''}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          </Overlay>
        )}
      </div>
    );
  };

  renderVideoOverview = () => {
    const { entryTitle, text } = this.props;

    return (
      <div className="rc-VideoOverview">
        <Grid container alignItems="center" spacing={24}>
          <Grid item md={6} sm={12}>
            {this.renderVideoScreen()}
          </Grid>
          <Grid item md={6} sm={12}>
            <div className="videoDescription">
              {entryTitle && (
                <React.Fragment>
                  {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
                  <Typography className="content-title" variant="h2">
                    {entryTitle}
                  </Typography>
                </React.Fragment>
              )}
              {text && <Markdown source={text} />}
              <Button variant="ghost" onClick={this.enableVideoPlayback} className="content-desc">
                {_t('Play video')}
              </Button>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  };

  render() {
    const { videoEmbedUrl } = this.props;

    if (!videoEmbedUrl) {
      return null;
    }

    return this.renderVideoOverview();
  }
}

export default VideoStory;
